/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';
import { formatTraceDataInTreeFormat } from '@/utils/tree-view-utils';
import { DataModel } from '@/ux-common/src/types/tree-view-types';
import { SpanDetailsProps } from 'types/traces';

import { useNarrative } from '@/hooks/useNarrative';
import { useTraces } from '@/hooks/useTraces';
import { OptimizationIcons } from '@/components/icons';
import BasicLoader from '@/components/spinner/basic-loader';

import ResizablePanels from './resizable-panels';
import RightPanel from './right-panel';

const SpanDetails: React.FC<SpanDetailsProps> = ({
  spanDetails,
  traceSpanLoading,
  traceSpanError,
  showErrorsOnly,
  showDataOnly,
  selectedSpanTypes,
  readOnly,
  mode,
  condition,
  isJsonViewOpen,
  setIsJsonViewOpen,
  fetchSingleTrace,
  allTracesForFactId,
  factId,
  factName,
  workflowName,
  showWorkflowNameInHeader,
  factNameForNarrativeGraph,
  shouldShowNarrativeView,
  tokenCount,
  tokenTooltip,
  traceTokenMap,
  enableTracePagination,
  tracePageSize,
  tracesTotalCount,
  tracesNextPageToken,
  tracesLoadingMore,
  onLoadMoreTraces,
}) => {
  // Track if user manually closed the JSON viewer
  const hasUserClosedJsonView = useRef(false);
  // Track if we've already fetched narrative data for this trace
  const hasFetchedNarrativeRef = useRef(false);
  const { messages } = useLanguage();
  const { filters } = useTraces() ?? {};

  const { state: narrativeState, actions: narrativeActions } = useNarrative();
  const {
    narrativeTurnsData,
    narrativeTurnsLoading,
    narrativeTurnsError,
    narrativeTurnsHasMore,
    narrativeTurnsFetchingMore,
  } = narrativeState;
  const { turnChildrenData, turnChildrenLoading } = narrativeState;
  const [exceptionSearchTerm, setExceptionSearchTerm] = useState<string>('');
  const [data, setData] = useState<DataModel[]>([]);
  const [jsonViewerData, setJsonViewerData] = useState({});
  const [treeViewData, setTreeViewData] = useState<DataModel[]>([]);
  const [selectedSpanId, setSelectedSpanId] = useState<string | null>(null);
  const [viewType, setViewType] = useState<
    'treeView' | 'ganttView' | 'narrativeView'
  >('treeView');
  const [loadingTraceId, setLoadingTraceId] = useState<string | null>(null);
  const [errorTraceId, setErrorTraceId] = useState<string | null>(null);
  const [errorTraceMessage, setErrorTraceMessage] = useState<string | null>(
    null
  );
  const params = useParams();

  const generateSpanLabel = (span: any): string => {
    const spanType = span.attributes?.['span.type']?.toLowerCase();
    const spanName = span.span_name;
    const entities: any[] = span.attributes?.entity || [];

    const firstEntity = entities[0];
    const fromAgent = span.attributes?.from_agent;
    const toAgent = span.attributes?.to_agent;

    // Identify specific entity types
    const agentEntity = entities.find((e: any) => e.type?.includes('agent'));
    const toolEntity = entities.find((e: any) => e.type?.includes('tool'));
    const vectorStoreEntity = entities.find((e: any) =>
      e.type?.includes('vectorstore')
    );

    switch (spanType) {
      case 'agentic.tool.invocation': {
        const agentName = agentEntity?.name || fromAgent;
        const toolName = toolEntity?.name || firstEntity?.tool_name;
        return agentName && toolName
          ? `${agentName} invokes ${toolName}`
          : spanName;
      }

      case 'agentic.mcp.invocation': {
        const agentName = agentEntity?.name || fromAgent;
        const toolName = toolEntity?.name || firstEntity?.tool_name;
        return agentName && toolName
          ? `${agentName} invokes mcp ${toolName}`
          : spanName;
      }

      case 'agentic.delegation': {
        const sendingAgent = firstEntity?.from_agent || fromAgent;
        const receivingAgent = firstEntity?.to_agent || toAgent;
        return sendingAgent && receivingAgent
          ? `${sendingAgent} delegates to ${receivingAgent}`
          : spanName;
      }

      case 'agentic.invocation': {
        const agentName = agentEntity?.name || fromAgent;
        return agentName || spanName;
      }

      case 'inference.framework': {
        const modelName = entities[1]?.name;
        const frameworkName = spanName ? spanName.split('.')[0] : null;
        return modelName && frameworkName
          ? `${modelName} with ${frameworkName}`
          : spanName;
      }

      case 'inference.modelapi': {
        const modelName = entities[1]?.name;
        const sdkName = spanName ? spanName.split('.')[0] : null;
        return modelName && sdkName ? `${modelName} with ${sdkName}` : spanName;
      }

      case 'inference': {
        const modelName = entities[1]?.name;
        return modelName || spanName;
      }

      case 'http': {
        const method = firstEntity?.method;
        const route = firstEntity?.route;
        return method && route ? `${method} ${route}` : spanName;
      }

      case 'retrieval': {
        const vectorStoreName =
          vectorStoreEntity?.name || vectorStoreEntity?.vector_store;
        const modelName = entities[1]?.name;
        return modelName && vectorStoreName
          ? `${modelName} on ${vectorStoreName}`
          : spanName;
      }

      case 'generic':
      default:
        return spanName;
    }
  };

  const checkSpanMeetsCriteria = (span: any): boolean => {
    if (!span.parent_id || span.parent_id.toLowerCase() === 'none') return true;
    // Determine span type
    let currentSpanType = span.attributes?.['span.type']?.toLowerCase();

    if (!currentSpanType) {
      currentSpanType = 'generic';
    }
    // Span type filter
    if (
      (selectedSpanTypes?.length ?? 0) > 0 &&
      !(selectedSpanTypes ?? [])
        .map((type) => type.toLowerCase())
        .some((type) => currentSpanType?.includes(type))
    ) {
      return false;
    }

    // Error and data filters
    const isErrorSpan = span.status?.code === 'error';
    const hasData = span.events?.some((event: any) =>
      event.name.startsWith('data.')
    );

    if (showDataOnly && showErrorsOnly) {
      // Both filters active: show spans with both or neither data events AND error status, based on condition
      return condition === 'have'
        ? hasData && isErrorSpan
        : !hasData && !isErrorSpan;
    } else if (showDataOnly) {
      // Only data filter: show spans with or without data events, based on condition
      return condition === 'have' ? hasData : !hasData;
    } else if (showErrorsOnly) {
      // Only error filter: show spans with or without error status, based on condition
      return condition === 'have' ? isErrorSpan : !isErrorSpan;
    }
    return true; // No filters active: all spans meet criteria
  };

  // Converts raw span data to the format needed by GanttChart
  const convertSpansToDataModel = (
    spans: any[],
    mode: 'highlight' | 'show-only'
  ): DataModel[] => {
    const matchingSpans = spans.filter((span) => checkSpanMeetsCriteria(span));

    // Created a set of matching span IDs for quick lookup
    const matchingSpanIds = new Set(matchingSpans.map((s) => s.span_id));

    const parentSpanIds = new Set(
      matchingSpans
        .map((span) => span.parent_id)
        .filter((id) => id && id.toLowerCase() !== 'none')
    );
    // Filter spans by mode
    const filteredSpans =
      mode === 'highlight'
        ? spans
        : spans.filter(
            (span) =>
              matchingSpans.includes(span) || parentSpanIds.has(span.span_id)
          );

    const spanIds = filteredSpans.map((span) => span.span_id);
    // find root span - span with no parent, else fallback to first span if all spans have a parent
    const rootSpan =
      spans.find(
        (span) => !span.parent_id || span.parent_id.toLowerCase() === 'none'
      ) ?? spans[0];

    const spanToDataModel: DataModel[] = filteredSpans.map((span, index) => {
      // Check if this span is a matching span
      const isMatching = matchingSpanIds.has(span.span_id);

      // Span color logic
      const spanColors =
        !isMatching && mode === 'show-only'
          ? 'rgba(50, 188, 175,0.3)'
          : span.status?.code === 'error'
            ? '#F26C74'
            : '#32BCAF';

      const spanType = span.attributes?.['span.type'] || '';
      const spanSubType = span.attributes?.['span.subtype'] || '';

      const displaySpanType =
        spanType || spanSubType
          ? [spanType, spanSubType].filter((item) => item !== '').join(' | ')
          : span.span_name;

      return {
        id: span.span_id,
        index,
        startTime: span.start_time,
        endTime: span.end_time,
        duration: span.duration_ms,
        label:
          generateSpanLabel(span) ||
          `${messages?.Traces?.trace_span_details?.span} ${index + 1}`,
        spanType,
        spanSubType,
        displaySpanType,
        description: `${messages?.General?.duration}: ${span.duration_ms}${messages?.Traces?.trace_details?.milli_seconds}`,
        // use parent if visible else use root span
        parentId:
          span.parent_id &&
          span.parent_id.toLowerCase() !== 'none' &&
          spanIds.includes(span.parent_id)
            ? span.parent_id
            : span.span_id === rootSpan?.span_id
              ? undefined
              : rootSpan?.span_id,
        children: [],
        color: spanColors,
        isFiltered:
          mode === 'highlight' ? !checkSpanMeetsCriteria(span) : false, // Marks spans that don't meet criteria
        entityCount: span.attributes?.['entity.count'] || 0,
        entity: span.attributes?.entity || [],
        statusCode: span.status?.code,
        statusMessage: span.status?.message,
        exceptionArrayType: span.events,
        method: span.attributes?.entity?.[0]?.method || '',
        route: span.attributes?.entity?.[0]?.route || '',
        monocleLang: span.attributes?.['monocle_apptrace.language'] || '',
        monocleVer: span.attributes?.['monocle_apptrace.version'] || '',
        attributes: span.attributes,
        events: span.events,
        isMCPSpan: span.attributes.is_mcp,
      };
    });

    return spanToDataModel;
  };

  // Click handler for spans
  const handleSpanClicked = (id: string) => {
    const findInTree = (nodes: DataModel[]): DataModel | null => {
      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children?.length) {
          const found = findInTree(node.children);
          if (found) return found;
        }
      }
      return null;
    };

    const selectedSpan = findInTree(data);

    if (!selectedSpan || selectedSpan.isFiltered) return;
    // Skip the fact node
    if (selectedSpan.spanType === 'fact') {
      return;
    }

    let originalSpan;

    // Grouped Traces
    if (spanDetails?.isGrouped && spanDetails?.traceSpanDetails) {
      if (selectedSpan.spanType === 'workflow') {
        const traceSpans = spanDetails.traceSpanDetails[id]?.spans || [];
        // Root span (span with no parent_id), fallback to first span for facts with no workflow root
        originalSpan =
          traceSpans.find((span: any) => !span.parent_id) || traceSpans[0];
      } else {
        // Search by span_id
        for (const traceId in spanDetails.traceSpanDetails) {
          const traceSpans = spanDetails.traceSpanDetails[traceId]?.spans || [];
          originalSpan = traceSpans.find((span: any) => span.span_id === id);
          if (originalSpan) break;
        }
      }
    } else {
      // single trace
      if (selectedSpan.spanType === 'workflow') {
        originalSpan = spanDetails?.spans?.find((span: any) => !span.parent_id);
      } else {
        originalSpan = spanDetails?.spans?.find((span) => span.span_id === id);
      }
    }
    setIsJsonViewOpen(true); // Always open on user click
    hasUserClosedJsonView.current = false; // Reset user intent on click
    setSelectedSpanId(id);
    setJsonViewerData(originalSpan || {});
  };

  //update the selected span when data changes
  useEffect(() => {
    if (data.length === 0) return;
    const initial =
      data[0].spanType === 'fact' ? data[0].children?.[0] : data[0];

    // Check if a specific trace is loading
    if (loadingTraceId) return;
    if (initial) {
      setSelectedSpanId(initial.id);
      let originalSpan;
      const id = initial.id;
      // Grouped Traces
      if (spanDetails?.isGrouped && spanDetails?.traceSpanDetails) {
        if (initial.spanType === 'workflow') {
          const traceSpans = spanDetails.traceSpanDetails[id]?.spans || [];
          originalSpan =
            traceSpans.find((span: any) => !span.parent_id) || traceSpans[0];
        } else {
          for (const traceId in spanDetails.traceSpanDetails) {
            const traceSpans =
              spanDetails.traceSpanDetails[traceId]?.spans || [];
            originalSpan = traceSpans.find((span: any) => span.span_id === id);
            if (originalSpan) break;
          }
        }
      } else {
        // single trace
        if (initial.spanType === 'workflow') {
          originalSpan = spanDetails?.spans?.find(
            (span: any) => !span.parent_id
          );
        } else {
          originalSpan = spanDetails?.spans?.find(
            (span) => span.span_id === id
          );
        }
      }
      setJsonViewerData(originalSpan || {});
    }
  }, [data]);

  useEffect(() => {
    let dataModelFormat: DataModel[] = [];
    if (
      spanDetails?.isGrouped &&
      allTracesForFactId &&
      allTracesForFactId.length > 0
    ) {
      const traceSpans = spanDetails?.traceSpanDetails || {};

      const nonWorkflowFacts = [
        'Inferences',
        'Agentic Turns',
        'Agent Invocations',
      ];
      const isNoWorkflowFact = nonWorkflowFacts.includes(factName ?? '');

      // Create workflow nodes for each trace
      const workflowNodes = allTracesForFactId
        .map((trace: any, index: number) => {
          const fetchedSpans = traceSpans[trace.trace_id]?.spans || [];

          if (fetchedSpans.length > 0) {
            const spans = convertSpansToDataModel(
              fetchedSpans,
              mode ?? 'highlight'
            );
            formatTraceDataInTreeFormat(spans);
            const root = spans.find((s) => !s.parentId) || spans[0];
            // for non workflow fact, return root span under fact header
            if (isNoWorkflowFact) {
              if (!root) return null; // Skip if no root span found
              return {
                ...root,
                parentId: factId ? `fact-${factId}` : undefined,
              };
            }
            // Create new children array with updated parentId
            const updatedChildren = root.children
              ? root.children.map((child) => ({
                  ...child,
                  parentId: trace.trace_id,
                }))
              : [];

            // Create workflow node
            const workflowNode = {
              ...root,
              id: trace.trace_id,
              workflowName: trace.workflow_name,
              traceId: trace.trace_id,
              label: `workflow: ${trace.workflow_name || root.label} | Trace: ${trace.trace_id}`,
              displaySpanType: `workflow: ${trace.workflow_name || root.displaySpanType} | Trace: ${trace.trace_id}`,
              parentId: factId ? `fact-${factId}` : undefined,
              tokenCount: traceTokenMap?.[trace.trace_id]?.count,
              tokenTooltip: traceTokenMap?.[trace.trace_id]?.tooltip,
              children: updatedChildren,
            };
            return workflowNode;
          } else {
            // Placeholder
            return {
              id: trace.trace_id,
              index: index,
              startTime: trace.start_time,
              endTime: trace.end_time,
              duration: trace.duration_ms,
              workflowName: trace.workflow_name,
              traceId: trace.trace_id,
              label: `workflow: ${trace.workflow_name || 'N/A'} | Trace: ${trace.trace_id}`,
              spanType: 'workflow',
              displaySpanType: `workflow: ${trace.workflow_name || 'N/A'} | Trace: ${trace.trace_id}`,
              description: `${messages?.General?.duration}: ${trace.duration_ms}${messages?.Traces?.trace_details?.milli_seconds}`,
              color: trace.status === 'success' ? '#32BCAF' : '#F26C74',
              statusCode: trace.status === 'success' ? 'ok' : 'error',
              attributes: { span_source: '' },
              events: [],
              exceptionArrayType: [],
              children: [],
              parentId: factId ? `fact-${factId}` : undefined,
              tokenCount: traceTokenMap?.[trace.trace_id]?.count,
              tokenTooltip: traceTokenMap?.[trace.trace_id]?.tooltip,
            };
          }
        })
        .filter(Boolean) as DataModel[]; // remove null values when there is no matching span

      const factLabel = factName ?? '';

      // Create FACT header node with workflows as children
      const factHeaderNode: DataModel = {
        id: `fact-${factId}`,
        index: 0,
        label: factLabel,
        spanType: 'fact',
        description: `${allTracesForFactId.length} trace${allTracesForFactId.length > 1 ? 's' : ''}`,
        startTime: '',
        endTime: '',
        duration: 0,
        color: '',
        attributes: { span_source: '' },
        statusCode: '',
        events: [],
        exceptionArrayType: [],
        children: workflowNodes,
      };

      dataModelFormat = [factHeaderNode];
    } else {
      // Single trace mode
      const spans = spanDetails?.spans
        ? convertSpansToDataModel(spanDetails.spans, mode ?? 'highlight')
        : [];

      formatTraceDataInTreeFormat(spans);

      const roots = spans.filter((s) => !s.parentId);
      // inject workflow name but skip for facts whose workflow name is shown at fact header instead
      if (roots.length > 0 && workflowName && !showWorkflowNameInHeader) {
        roots[0].workflowName = workflowName;
        roots[0].displaySpanType = `workflow: ${workflowName}`;
      }
      dataModelFormat = roots.length ? roots : spans.slice(0, 1);
    }

    setData(dataModelFormat);
  }, [
    spanDetails?.spans,
    spanDetails?.traceSpanDetails,
    spanDetails?.isGrouped,
    allTracesForFactId,
    showErrorsOnly,
    showDataOnly,
    selectedSpanTypes,
    mode,
    condition,
    factName,
    factId,
    traceTokenMap,
  ]);

  useEffect(() => {
    const rootNodes = data
      .filter((trace) => !trace.parentId) //exclude child nodes from rendering, they will be rendered under their parents, we only render all roots.
      .map((node) => {
        if (node.spanType === 'fact') {
          const firstSpan =
            spanDetails?.traceSpanDetails?.[allTracesForFactId?.[0]?.trace_id]
              ?.spans?.[0];

          // Fall back to old label if spans are not yet fetched
          if (!firstSpan) return { ...node };

          const sessionId =
            firstSpan?.attributes?.['scope.agentic.session'] || 'N/A';
          const traceId = firstSpan?.trace_id || 'N/A';
          const parentId = firstSpan?.parent_id || 'N/A';

          const factHeaderLabel =
            factName === 'Agentic Turns'
              ? `Session ID: ${sessionId} | Trace ID: ${traceId}`
              : ['Agent Invocations', 'Inferences'].includes(factName ?? '')
                ? `Trace ID: ${traceId} | Parent ID: ${parentId}`
                : (factName ?? '');

          return { ...node, label: factHeaderLabel };
        }
        return { ...node };
      });
    setTreeViewData(rootNodes);
  }, [data, factName, spanDetails, allTracesForFactId]);

  // Reset fetch tracking and clear stale turns data when trace/factId changes (pagination)
  // For single traces: spanDetails?.trace_id changes on pagination
  // For grouped data: factId changes on pagination
  useEffect(() => {
    hasFetchedNarrativeRef.current = false;
    narrativeActions.clearNarrativeTurns();
    narrativeActions.clearTurnChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factId, spanDetails?.trace_id]);

  // Reset fetch tracking and clear stale data when time range or factName changes
  useEffect(() => {
    hasFetchedNarrativeRef.current = false;
    narrativeActions.clearNarrativeTurns();
    narrativeActions.clearTurnChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factNameForNarrativeGraph]);

  // Fetch narrative turns when viewType changes to narrativeView
  useEffect(() => {
    if (
      viewType === 'narrativeView' &&
      !narrativeTurnsData &&
      !hasFetchedNarrativeRef.current
    ) {
      // Use factId for grouped data, or spanDetails.trace_id for single trace view
      const effectiveFactId = factId || spanDetails?.trace_id;

      if (effectiveFactId) {
        hasFetchedNarrativeRef.current = true;
        const appName = Array.isArray(params.slug)
          ? params.slug[0]
          : params.slug;
        narrativeActions.fetchNarrativeTurns({
          app_name: appName,
          filter_fact_id: effectiveFactId,
          filter_fact: factNameForNarrativeGraph || '',
          include_first_graph: true,
          // Same fact time window the traces/spans APIs use, so the backend can
          // partition-prune the turns query instead of scanning the full table.
          start_time: filters?.timeRange?.startTime,
          end_time: filters?.timeRange?.endTime,
        });
      } else {
        console.log(
          '[Narrative Fetch] Missing required parameters (app_name or factId)'
        );
      }
    }
  }, [
    viewType,
    narrativeTurnsData,
    workflowName,
    spanDetails,
    narrativeActions,
    factId,
    spanDetails?.trace_id,
  ]);

  // Callback for when a turn node is expanded in the diagram and needs children fetched.
  // Also auto-prefetches the next page of turns when the last loaded turn is expanded.
  const handleExpandTurn = useCallback(
    (turnNodeId: string, traceId: string) => {
      if (!turnChildrenData[turnNodeId] && !turnChildrenLoading[turnNodeId]) {
        // The turns response carries each turn's own start/end; pass them so
        // the graph's spans query is partition-pruned to this turn's window.
        const turnDetails = (narrativeTurnsData?.turn_ids ?? []).find(
          (t) => turnNodeId in t
        )?.[turnNodeId];
        narrativeActions.fetchTurnChildren({
          workflow_name: workflowName,
          trace_id: traceId,
          turn_id: turnNodeId,
          start_time: turnDetails?.start_time,
          end_time: turnDetails?.end_time,
        });
      }

      const turnIds = narrativeTurnsData?.turn_ids ?? [];
      const lastEntry = turnIds[turnIds.length - 1];
      const lastTurnId = lastEntry ? Object.keys(lastEntry)[0] : undefined;
      if (
        lastTurnId === turnNodeId &&
        narrativeTurnsHasMore &&
        !narrativeTurnsFetchingMore
      ) {
        const effectiveFactId = factId || spanDetails?.trace_id;
        const appName = Array.isArray(params.slug)
          ? params.slug[0]
          : params.slug;
        if (effectiveFactId && appName) {
          narrativeActions.fetchMoreNarrativeTurns({
            app_name: appName,
            filter_fact_id: effectiveFactId,
            filter_fact: factNameForNarrativeGraph || '',
            include_first_graph: false,
          });
        }
      }
    },
    [
      turnChildrenData,
      turnChildrenLoading,
      narrativeActions,
      workflowName,
      narrativeTurnsData,
      narrativeTurnsHasMore,
      narrativeTurnsFetchingMore,
      factId,
      spanDetails?.trace_id,
      factNameForNarrativeGraph,
      params.slug,
    ]
  );

  const handleCloseRightPanel = useCallback(() => {
    setIsJsonViewOpen(false);
    setJsonViewerData({});
    hasUserClosedJsonView.current = true;
  }, [setIsJsonViewOpen]);

  const rightPanel = (
    <RightPanel
      selectedNode={jsonViewerData}
      onClose={handleCloseRightPanel}
      exceptionSearchTerm={exceptionSearchTerm}
      setExceptionSearchTerm={setExceptionSearchTerm}
    />
  );
  const handleTraceExpand = async (traceOrId: any) => {
    let trace = traceOrId;
    if (typeof traceOrId === 'string') {
      trace = allTracesForFactId?.find((t: any) => t.trace_id === traceOrId);
    }
    if (trace) {
      // clear existing error before starting a new fetch
      setErrorTraceId(null);
      setErrorTraceMessage(null);
      const result = fetchSingleTrace && (await fetchSingleTrace(trace));
      // on failure, identify node ID to show error icon and stop loading
      if (result !== true) {
        const traceId =
          typeof traceOrId === 'string' ? traceOrId : trace.trace_id;
        setErrorTraceId(traceId);
        setErrorTraceMessage(
          result?.data?.error?.message ||
            result?.message ||
            'Error loading traces'
        );
        setLoadingTraceId(null);
      }
    }
  };

  return (
    <>
      {traceSpanLoading ? (
        <div className="mt-2 flex h-[414px] flex-1 items-center justify-center border border-[#0A343C]">
          <BasicLoader />
        </div>
      ) : traceSpanError ? (
        <div className="flex h-[410px] flex-col items-center justify-center gap-2 text-body text-body-primary">
          {OptimizationIcons.ErrorsCircle('size-12 text-[#40565A]')}
          {messages?.Traces.trace_span_details.error_loading_spans}
        </div>
      ) : (
        <ResizablePanels
          setExceptionSearchTerm={setExceptionSearchTerm}
          readOnly={readOnly}
          viewType={viewType}
          setViewType={setViewType}
          onClick={handleSpanClicked}
          data={data}
          treeViewData={treeViewData}
          rightPanel={rightPanel}
          isRightPanelOpen={isJsonViewOpen}
          selectedSpanId={selectedSpanId}
          narrativeTurnsData={narrativeTurnsData}
          narrativeTurnsLoading={narrativeTurnsLoading}
          narrativeTurnsError={narrativeTurnsError}
          narrativeTurnsFetchingMore={narrativeTurnsFetchingMore}
          fetchSingleTrace={handleTraceExpand}
          allTracesForFactId={allTracesForFactId ?? []}
          factName={factNameForNarrativeGraph || ''}
          loadingTraceId={loadingTraceId}
          setLoadingTraceId={setLoadingTraceId}
          errorTraceId={errorTraceId}
          errorTraceMessage={errorTraceMessage}
          factId={factId ?? null}
          turnChildrenData={turnChildrenData}
          turnChildrenLoading={turnChildrenLoading}
          spanDetails={spanDetails || undefined}
          onExpandTurn={handleExpandTurn}
          shouldShowNarrativeView={shouldShowNarrativeView}
          tokenCount={tokenCount}
          tokenTooltip={tokenTooltip}
          enableTracePagination={enableTracePagination}
          tracePageSize={tracePageSize}
          tracesTotalCount={tracesTotalCount}
          tracesNextPageToken={tracesNextPageToken}
          tracesLoadingMore={tracesLoadingMore}
          onLoadMoreTraces={onLoadMoreTraces}
        />
      )}
    </>
  );
};

export { SpanDetails };
